/**
 * StageAcknowledgementPanel — per-candidate, per-round acknowledgement box
 * stack, shown inside the Apply Matching tab's candidate card (below the
 * skills/experience/education row).
 *
 * Fetches GET /matching/jobs/{jobId}/candidates/{resumeId}/stage-acknowledgements
 * — one entry per pipeline round already resolved (Selected/Rejected) for
 * this candidate. Renders nothing at all when that list is empty (no rounds
 * resolved yet), loading, or errored, so a candidate with nothing to
 * acknowledge doesn't add visual weight to an already-dense card.
 *
 * Send/Resend button + loading state + "Sent {date}"/"Acknowledged {date}"
 * text follows the same visual language as the Live Interview summary page's
 * acknowledgement button (SummaryPage.tsx: handleSendAcknowledgement /
 * "Resend Acknowledgement Mail" + green "Sent {date}" badge) — reused here
 * per-round instead of per-session.
 */

import React, { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { EnvelopeIcon, CheckCircleIcon, ClockIcon, ChatBubbleLeftIcon } from '@heroicons/react/24/outline'
import { matchingApi, type StageAcknowledgement } from '../../services/api'
import CollapsibleCard from '../common/CollapsibleCard'

interface StageAcknowledgementPanelProps {
  jobId: string
  resumeId: string
}

// Fixed pipeline order — whichever subset of these round codes is present in
// the response is shown, in this order. Anything unrecognized is appended
// at the end (stable) rather than dropped, so a future round code never
// silently disappears.
const ROUND_ORDER = ['SCREENING', 'L1', 'L2', 'L3', 'HR', 'OFFER', 'BGV']

const sortByPipelineOrder = (rounds: StageAcknowledgement[]): StageAcknowledgement[] => {
  const indexOf = (code: string) => {
    const idx = ROUND_ORDER.indexOf((code || '').toUpperCase())
    return idx === -1 ? ROUND_ORDER.length : idx
  }
  return [...rounds].sort((a, b) => indexOf(a.round_code) - indexOf(b.round_code))
}

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

const DecisionBadge: React.FC<{ decision: StageAcknowledgement['decision'] }> = ({ decision }) => {
  const isSelected = decision === 'Selected'
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${
        isSelected ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
      }`}
    >
      {decision}
    </span>
  )
}

const RoundBox: React.FC<{
  round: StageAcknowledgement
  sending: boolean
  onSend: (roundCode: string) => void
}> = ({ round, sending, onSend }) => {
  const isSent = round.status === 'sent'
  const isAcknowledged = round.status === 'acknowledged'
  const buttonLabel = round.status === 'not_sent' ? 'Send Acknowledgement Mail' : 'Resend Acknowledgement Mail'

  return (
    <div className="border border-gray-200 rounded-md px-3 py-2 bg-white space-y-1.5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-semibold text-gray-800 truncate">{round.label}</span>
          <DecisionBadge decision={round.decision} />
          {isSent && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-700">
              <ClockIcon className="w-3 h-3" /> Acknowledgement In Progress
            </span>
          )}
          {isAcknowledged && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-100 text-green-700">
              <CheckCircleIcon className="w-3 h-3" /> Acknowledged
            </span>
          )}
          {isSent && round.sent_at && (
            <span className="text-[11px] text-gray-500 whitespace-nowrap">Sent {formatDate(round.sent_at)}</span>
          )}
          {isAcknowledged && round.acknowledged_at && (
            <span className="text-[11px] text-gray-500 whitespace-nowrap">Acknowledged {formatDate(round.acknowledged_at)}</span>
          )}
        </div>

        <button
          onClick={() => onSend(round.round_code)}
          disabled={sending}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white text-gray-700 border border-gray-300 rounded-md text-xs font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
        >
          {sending ? (
            <>
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-500" />
              Sending…
            </>
          ) : (
            <>
              <EnvelopeIcon className="w-3.5 h-3.5" />
              {buttonLabel}
            </>
          )}
        </button>
      </div>

      {round.feedback_text && (
        <div className="flex items-start gap-1.5 bg-gray-50 border border-gray-100 rounded px-2 py-1.5">
          <ChatBubbleLeftIcon className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-0.5" />
          <p className="text-[11px] text-gray-600 whitespace-pre-wrap break-words">
            <span className="font-medium text-gray-500">Candidate feedback: </span>
            {round.feedback_text}
          </p>
        </div>
      )}
    </div>
  )
}

const StageAcknowledgementPanel: React.FC<StageAcknowledgementPanelProps> = ({ jobId, resumeId }) => {
  const queryClient = useQueryClient()
  const [sendingRound, setSendingRound] = useState<string | null>(null)

  const queryKey = ['stage-acknowledgements', jobId, resumeId]

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => matchingApi.getStageAcknowledgements(jobId, resumeId).then((r) => r.data),
    enabled: !!jobId && !!resumeId,
    // A decision made elsewhere (Approve/Reject on a Live or Proctored
    // scorecard, a status change) is what makes a round appear here, so
    // always re-check when the panel is shown instead of trusting a cache.
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })

  const handleSend = async (roundCode: string) => {
    if (sendingRound) return
    setSendingRound(roundCode)
    try {
      await matchingApi.sendStageAcknowledgement(jobId, resumeId, roundCode)
      const sentAt = new Date().toISOString()
      queryClient.setQueryData<StageAcknowledgement[] | undefined>(queryKey, (old) =>
        old?.map((r) =>
          r.round_code === roundCode
            ? { ...r, status: 'sent', sent_at: r.sent_at ?? sentAt, last_sent_at: sentAt, resend_count: r.resend_count + (r.status === 'not_sent' ? 0 : 1) }
            : r
        )
      )
      toast.success('Acknowledgement mail sent')
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to send the acknowledgement mail. Please try again.')
    } finally {
      setSendingRound(null)
    }
  }

  if (isLoading) {
    return (
      <div className="mt-2 flex items-center gap-2 px-1 py-1.5">
        <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-violet-500 flex-shrink-0" />
        <span className="text-xs text-gray-400">Loading stage acknowledgements…</span>
      </div>
    )
  }

  if (isError || !data || data.length === 0) return null

  const rounds = sortByPipelineOrder(data)

  return (
    <div className="mt-2">
      <CollapsibleCard title="Stage Acknowledgements" compact defaultOpen>
        <div className="space-y-1.5">
          {rounds.map((round) => (
            <RoundBox
              key={round.round_code}
              round={round}
              sending={sendingRound === round.round_code}
              onSend={handleSend}
            />
          ))}
        </div>
      </CollapsibleCard>
    </div>
  )
}

export default StageAcknowledgementPanel
